import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { FileText, Upload, AlertTriangle, Download, MoreVertical, Clock } from 'lucide-react';
import {
  certificatesApi,
  type Certificate,
  type CertificateType,
  type CertificateStatus,
  type MaterialExpiry,
  formatCertificateType,
  isExpiringSoon,
  getDaysUntilExpiry,
} from '../api/services/certificates.api';
import { useCertificatesList, useExpiringCertificates, useDeleteCertificate } from '../api/hooks';
import { Button } from '../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Dialog, DialogContent } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import {
  PageHeader,
  StatusBadge,
  MetricCard,
  KPIGrid,
  DataTable,
  type Column,
  TableFilters,
} from '../components/common';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { getErrorMessage } from '../api/client';
import { toast } from 'sonner';
import { CertificateUploadForm } from '../components/certificates/CertificateUploadForm';

// Certificate status mapping for StatusBadge
const certificateStatusMap: Record<string, string> = {
  VALID: 'approved',
  ACTIVE: 'approved',
  EXPIRED: 'expired',
  REVOKED: 'cancelled',
  PENDING_VERIFICATION: 'pending',
  PENDING_RENEWAL: 'pending',
};

/**
 * Material certificates, and the batches whose material expires.
 *
 * These were two pages — /certificates and /compliance — listing the same
 * Certificate records through two different fetch patterns with two different
 * sets of columns, so the same certificate looked like two records depending on
 * which door the reader came through. This is the surviving one, because the
 * record page both lists linked to already lived at /certificates/:id.
 *
 * Material expiry came across as a tab rather than a third page. It is a
 * different record — a batch with a shelf life, not a document — but it is the
 * same question asked of stock rather than of paperwork, and the person chasing
 * one is chasing the other.
 */
const expiryStatusColors: Record<string, string> = {
  VALID: 'bg-status-success-subtle text-status-success-foreground',
  EXPIRING_SOON: 'bg-status-warning-subtle text-status-warning-foreground',
  EXPIRED: 'bg-status-destructive-subtle text-status-destructive-foreground',
  DISPOSED: 'bg-status-neutral-subtle text-status-neutral-foreground',
};

export default function Certificates() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<CertificateType | ''>('');
  const [statusFilter, setStatusFilter] = useState<CertificateStatus | ''>('');
  const [expiryFilter, setExpiryFilter] = useState<string>('');
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [materialExpiries, setMaterialExpiries] = useState<MaterialExpiry[]>([]);
  const [expiriesLoading, setExpiriesLoading] = useState(true);
  const [expiryStatusFilter, setExpiryStatusFilter] = useState<string>('');

  useEffect(() => {
    certificatesApi
      .getMaterialExpiries({ limit: 100 })
      .then((res) => setMaterialExpiries(res.data))
      .catch((error) => toast.error(getErrorMessage(error)))
      .finally(() => setExpiriesLoading(false));
  }, []);

  // React Query hooks
  const { data: certificatesData, isLoading } = useCertificatesList({
    search: searchQuery || undefined,
    type: typeFilter || undefined,
    status: statusFilter || undefined,
  });

  // Fetch expiring certificates for alert
  const { data: expiringCerts } = useExpiringCertificates(90);

  // Delete mutation
  const deleteMutation = useDeleteCertificate();

  // Download document
  const handleDownload = async (cert: Certificate) => {
    try {
      const blob = await certificatesApi.downloadDocument(cert.id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = cert.documentName || `${cert.certificateNumber}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Document downloaded');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to download document');
    }
  };

  // Column definitions for DataTable
  const certificateColumns: Column<Certificate>[] = [
    {
      key: 'certificateNumber',
      linkTo: (cert) => `/certificates/${cert.id}`,
      header: 'Certificate #',
      sortable: true,
      render: (cert) => <span className="font-medium">{cert.certificateNumber}</span>,
    },
    {
      key: 'type',
      header: 'Type',
      sortable: true,
      render: (cert) => (
        <div className="flex flex-col gap-1">
          <Badge variant="outline">{formatCertificateType(cert.type)}</Badge>
          {cert.subType && (
            <Badge variant="secondary" className="text-xs">
              EN {cert.subType}
            </Badge>
          )}
        </div>
      ),
    },
    {
      key: 'heatNumber',
      header: 'Heat / Lot #',
      render: (cert) => (
        <div className="text-sm">
          {cert.heatNumber && <div className="font-mono">H: {cert.heatNumber}</div>}
          {cert.lotNumber && <div className="font-mono">L: {cert.lotNumber}</div>}
          {!cert.heatNumber && !cert.lotNumber && <span className="text-muted-foreground">-</span>}
        </div>
      ),
    },
    {
      key: 'issuedDate',
      header: 'Issue Date',
      sortable: true,
      render: (cert) => format(new Date(cert.issuedDate), 'MMM dd, yyyy'),
    },
    {
      key: 'expiryDate',
      header: 'Expiry Date',
      sortable: true,
      render: (cert) =>
        cert.expiryDate ? (
          <div className="flex items-center gap-2">
            <span
              className={
                isExpiringSoon(cert.expiryDate, 90)
                  ? isExpiringSoon(cert.expiryDate, 30)
                    ? 'text-red-600 font-medium'
                    : 'text-amber-600 font-medium'
                  : ''
              }
            >
              {format(new Date(cert.expiryDate), 'MMM dd, yyyy')}
            </span>
            {isExpiringSoon(cert.expiryDate, 90) && (
              <AlertTriangle className="h-4 w-4 text-amber-600" />
            )}
          </div>
        ) : (
          <span className="text-muted-foreground">No expiry</span>
        ),
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (cert) => (
        <StatusBadge
          status={certificateStatusMap[cert.status] || 'pending'}
          label={cert.status.replace(/_/g, ' ')}
        />
      ),
    },
    {
      key: 'documentUrl',
      header: 'Document',
      render: (cert) =>
        cert.documentUrl ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              handleDownload(cert);
            }}
          >
            <FileText className="h-4 w-4 mr-1" />
            View
          </Button>
        ) : (
          <span className="text-muted-foreground text-sm">No file</span>
        ),
    },
    {
      key: 'actions',
      header: 'Actions',
      className: 'text-right',
      render: (cert) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
            <Button variant="ghost" size="sm">
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {cert.documentUrl && (
              <DropdownMenuItem onClick={() => handleDownload(cert)}>
                <Download className="h-4 w-4 mr-2" />
                Download Document
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => {
                if (confirm('Are you sure you want to delete this certificate?')) {
                  deleteMutation.mutate(cert.id);
                }
              }}
              className="text-red-600"
            >
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  const certificates = certificatesData?.data || [];

  // Filter by expiry
  const filteredCertificates = certificates.filter((cert: Certificate) => {
    if (!expiryFilter) return true;
    const days = parseInt(expiryFilter, 10);
    return isExpiringSoon(cert.expiryDate, days);
  });

  // Calculate expiring counts
  const expiringCertsList = expiringCerts ?? [];
  const critical = expiringCertsList.filter(
    (c: Certificate) => getDaysUntilExpiry(c.expiryDate) <= 30
  ).length;
  const warning = expiringCertsList.filter((c: Certificate) => {
    const days = getDaysUntilExpiry(c.expiryDate);
    return days > 30 && days <= 60;
  }).length;
  const info = expiringCertsList.filter(
    (c: Certificate) => getDaysUntilExpiry(c.expiryDate) > 60
  ).length;

  const filteredExpiries = expiryStatusFilter
    ? materialExpiries.filter((e) => e.status === expiryStatusFilter)
    : materialExpiries;

  const materialExpiryColumns: Column<MaterialExpiry>[] = [
    {
      key: 'item',
      header: 'Item',
      render: (exp) => (
        <div>
          <p className="font-medium">{exp.item.name}</p>
          <p className="text-xs text-muted-foreground">{exp.item.sku}</p>
        </div>
      ),
    },
    { key: 'batchNumber', header: 'Batch #', render: (exp) => <>{exp.batchNumber || '—'}</> },
    { key: 'location', header: 'Location', render: (exp) => <>{exp.location.name}</> },
    { key: 'quantity', header: 'Quantity', sortable: true },
    {
      key: 'expiryDate',
      header: 'Expiry Date',
      sortable: true,
      render: (exp) => <>{format(new Date(exp.expiryDate), 'MMM d, yyyy')}</>,
    },
    {
      key: 'daysUntilExpiry',
      header: 'Days Left',
      sortable: true,
      render: (exp) =>
        exp.daysUntilExpiry !== undefined ? (
          <span
            className={
              exp.daysUntilExpiry < 0
                ? 'font-medium text-status-destructive-foreground'
                : exp.daysUntilExpiry <= 30
                  ? 'font-medium text-status-warning-foreground'
                  : ''
            }
          >
            {exp.daysUntilExpiry < 0
              ? `${Math.abs(exp.daysUntilExpiry)} days overdue`
              : `${exp.daysUntilExpiry} days`}
          </span>
        ) : (
          // Undefined is not zero days left. A batch with no expiry recorded is
          // unmeasured, and showing it as due today would send somebody to a
          // rack for nothing.
          <span className="text-muted-foreground">Not recorded</span>
        ),
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (exp) => (
        <Badge className={expiryStatusColors[exp.status]}>{exp.status.replace('_', ' ')}</Badge>
      ),
    },
  ];

  return (
    <div className="space-y-5">
      {/* Header */}
      <PageHeader
        title="Material Certificates"
        description="Mill test reports and conformance documents held against stock, with heat numbers and expiry dates."
        actions={
          <Button onClick={() => setUploadDialogOpen(true)}>
            <Upload className="h-4 w-4 mr-2" />
            Upload Certificate
          </Button>
        }
      />

      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <CertificateUploadForm onSuccess={() => setUploadDialogOpen(false)} />
        </DialogContent>
      </Dialog>

      {/* KPI Cards */}
      <KPIGrid columns={4}>
        <MetricCard
          label="Total Certificates"
          value={certificates.length}
          icon={<FileText className="h-5 w-5" />}
          sentiment="info"
        />
        <MetricCard
          label="Expiring Soon"
          value={
            certificates.filter(
              (c: Certificate) =>
                c.expiryDate && isExpiringSoon(c.expiryDate, 90) && !isExpiringSoon(c.expiryDate, 0)
            ).length
          }
          icon={<Clock className="h-5 w-5" />}
          sentiment="warning"
        />
        <MetricCard
          label="Expired"
          value={
            certificates.filter(
              (c: Certificate) => c.status === 'EXPIRED' || c.status === 'REVOKED'
            ).length
          }
          icon={<AlertTriangle className="h-5 w-5" />}
          sentiment="danger"
        />
        <MetricCard
          label="Pending Verification"
          value={
            certificates.filter(
              (c: Certificate) =>
                c.status === 'PENDING_VERIFICATION' || c.status === 'PENDING_RENEWAL'
            ).length
          }
          icon={<Clock className="h-5 w-5" />}
          sentiment="warning"
        />
      </KPIGrid>

      {/* Expiry Alerts */}
      {(critical > 0 || warning > 0 || info > 0) && (
        <div className="space-y-4">
          {critical > 0 && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Critical: {critical} Certificate(s) Expiring in 30 Days</AlertTitle>
              <AlertDescription>
                <button
                  onClick={() => setExpiryFilter('30')}
                  className="underline hover:text-red-800"
                >
                  View certificates
                </button>
              </AlertDescription>
            </Alert>
          )}
          {warning > 0 && (
            <Alert className="border-amber-200 bg-amber-50">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              <AlertTitle className="text-amber-900">
                Warning: {warning} Certificate(s) Expiring in 60 Days
              </AlertTitle>
              <AlertDescription className="text-amber-800">
                <button
                  onClick={() => setExpiryFilter('60')}
                  className="underline hover:text-amber-900"
                >
                  View certificates
                </button>
              </AlertDescription>
            </Alert>
          )}
        </div>
      )}

      {/* Table */}
      {/* Two record types, one question. The certificate list and the batch
          expiry list used to be separate pages; they are tabs now because the
          person chasing a lapsed mill cert is the person chasing a batch about
          to go out of date. */}
      <Tabs defaultValue="certificates" className="space-y-4">
        <TabsList>
          <TabsTrigger value="certificates">Certificates</TabsTrigger>
          <TabsTrigger value="material-expiry">Material expiry</TabsTrigger>
        </TabsList>

        <TabsContent value="certificates">
          <DataTable<Certificate>
            toolbar={
              <TableFilters
                search={searchQuery}
                onSearchChange={setSearchQuery}
                searchPlaceholder="Search certificates..."
                filters={[
                  {
                    id: 'type',
                    label: 'Types',
                    value: typeFilter || 'all',
                    options: [
                      { value: 'MILL_TEST_CERTIFICATE', label: 'MTC (Mill Test)' },
                      { value: 'CERTIFICATE_OF_CONFORMANCE', label: 'CoC (Conformance)' },
                      { value: 'API_Q1', label: 'API Q1' },
                      { value: 'API_Q2', label: 'API Q2' },
                      { value: 'NACE_MR0175', label: 'NACE MR0175' },
                      { value: 'ISO_CERTIFICATION', label: 'ISO Certification' },
                    ],
                    onChange: (val) => setTypeFilter(val === 'all' ? '' : (val as CertificateType)),
                    placeholder: 'All Types',
                    width: 'w-[180px]',
                  },
                  {
                    id: 'status',
                    label: 'Statuses',
                    value: statusFilter || 'all',
                    options: [
                      { value: 'VALID', label: 'Valid' },
                      { value: 'ACTIVE', label: 'Active' },
                      { value: 'EXPIRED', label: 'Expired' },
                      { value: 'PENDING_VERIFICATION', label: 'Pending Verification' },
                      { value: 'REVOKED', label: 'Revoked' },
                    ],
                    onChange: (val) =>
                      setStatusFilter(val === 'all' ? '' : (val as CertificateStatus)),
                    placeholder: 'All Statuses',
                    width: 'w-[160px]',
                  },
                  {
                    id: 'expiry',
                    label: 'Expiry',
                    value: expiryFilter || 'all',
                    options: [
                      { value: '30', label: '30 days' },
                      { value: '60', label: '60 days' },
                      { value: '90', label: '90 days' },
                    ],
                    onChange: (val) => setExpiryFilter(val === 'all' ? '' : val),
                    placeholder: 'Expiring Within',
                    width: 'w-[160px]',
                  },
                ]}
              />
            }
            data={filteredCertificates}
            columns={certificateColumns}
            loading={isLoading}
            onRowClick={(cert) => navigate(`/certificates/${cert.id}`)}
            pagination
            exportable
            columnToggle
            emptyTitle="No certificates found"
            emptyDescription="Upload a certificate to get started."
          />
        </TabsContent>

        <TabsContent value="material-expiry">
          <DataTable
            data={filteredExpiries}
            columns={materialExpiryColumns}
            loading={expiriesLoading}
            toolbar={
              <TableFilters
                filters={[
                  {
                    id: 'expiryStatus',
                    label: 'Statuses',
                    value: expiryStatusFilter || 'all',
                    options: [
                      { value: 'VALID', label: 'Valid' },
                      { value: 'EXPIRING_SOON', label: 'Expiring soon' },
                      { value: 'EXPIRED', label: 'Expired' },
                      { value: 'DISPOSED', label: 'Disposed' },
                    ],
                    onChange: (val) => setExpiryStatusFilter(val === 'all' ? '' : val),
                    placeholder: 'All statuses',
                    width: 'w-[180px]',
                  },
                ]}
              />
            }
            pagination
            exportable
            columnToggle
            emptyTitle="Nothing expiring"
            emptyDescription="No batch on file carries a shelf life that has run out or is about to."
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
